import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { CustomerProductView } from '../ProductsTab/ProductDetailModal';
import type { ProductDraft } from '../ProductsTab/ProductDialog';

interface CustomerPreviewModalProps {
  open: boolean;
  onClose: () => void;
  product: ProductDraft | null;
  imageUrl?: string;
}

/**
 * Read-only customer-storefront preview of a product, used from the
 * catalogue reorder view. The whole point is to show partners exactly
 * what their end customers will see when clicking a card — so this is
 * pure render with zero edit affordances.
 *
 * Reuses CustomerProductView from ProductDetailModal so the partner-side
 * preview stays in lockstep with whatever ships on the storefront.
 */
export function CustomerPreviewModal({ open, onClose, product, imageUrl }: CustomerPreviewModalProps) {
  if (!open || !product) return null;
  return (
    <DialogPrimitive.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-50 w-[95vw] max-w-3xl max-h-[92vh] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden focus:outline-none"
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className="sr-only">
            {product.name || 'Customer preview'}
          </DialogPrimitive.Title>

          {/* Header strip — read-only label + close button */}
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-3 flex-shrink-0">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                Customer preview
              </div>
              <div className="text-sm text-slate-700 truncate">
                This is how this product appears in your customer's storefront.
              </div>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </DialogPrimitive.Close>
          </div>

          <div className="flex-1 overflow-auto bg-slate-50">
            <CustomerProductView product={product} imageUrl={imageUrl} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default CustomerPreviewModal;
